<!-- eslint-disable vue/multi-word-component-names -->
<!-- Layout filenames are fixed by the house structure spec (Default / Blank / Topbar),
     so the multi-word rule cannot apply to this folder.

     One column, no sidebar, no drawer. This portal is held in one hand in a corridor:
     the reception layout's chrome — 64 px of navigation rail plus a hamburger that
     opens a second copy of it — would spend the screen on getting somewhere, and there
     is only one place to be. Navigation appears here when there is a second screen to
     navigate to, and not before: a housekeeper has one screen and sees no tabs at all,
     a supervisor has two and gets them. -->
<template>
    <div class="flex min-h-dvh flex-col bg-muted">
        <Topbar />

        <nav
            v-if="showNavigation"
            class="sticky top-16 z-10 flex shrink-0 gap-1 border-b border-default bg-default px-2 py-1.5 sm:px-4"
        >
            <UButton
                v-for="item in navigation"
                :key="item.name"
                :to="{ name: item.name }"
                :label="item.label"
                :icon="item.icon"
                :color="route.name === item.name ? 'primary' : 'neutral'"
                :variant="route.name === item.name ? 'subtle' : 'ghost'"
                :ui="{ label: 'truncate' }"
                size="lg"
                class="min-w-0 flex-1 justify-center gap-1.5 px-1.5"
            />
        </nav>

        <!-- The bottom padding is the height of the bar iOS floats over the page. Without
             it the last floor of the round sits underneath Safari's own address bar and
             simply is not there: reported as "I cannot see all the floors", which was
             true. -->
        <main class="min-w-0 flex-1 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom)+3rem)] sm:p-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <slot>
                <RouterView />
            </slot>
        </main>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import Topbar from '@/layouts/components/Topbar.vue'
    import { Permission } from '@/enums/permission'
    import { StaffModule } from '@/enums/staff'
    import { useAppStore } from '@/stores/app'

    const { t }    = useI18n()
    const route    = useRoute()
    const appStore = useAppStore()

    /**
     * Each tab is gated by what it needs, and the strip appears when more than one
     * survives. That is what makes a single-purpose account single-purpose: a
     * housekeeper has one screen and sees no tabs, a handyman has one screen and sees
     * none either — a different one — and a supervisor who also mends things gets all
     * five.
     *
     * Two kinds of gate, and the second arrived with "Moj urnik" and "Moja ura".
     *
     * A `permission` is what the ACCOUNT may do, and it already carries the licence for
     * every other tab here: a permission belonging to a module the hotel has not bought
     * is dropped where permissions are resolved (CLAUDE.md deviation 15), so the tab
     * hides itself with no special case.
     *
     * Those two have no permission at all — reading your own week and stamping your own
     * hours are not rights somebody grants (docs/OSEBJE.md §13) — so there is nothing
     * for the mask to take away, and the licence has to be asked for directly. It
     * matters more here than a missing tab would suggest: a first call in a house
     * without `staff` comes back 403 with code 19, and that locks the whole portal
     * behind ModuleNotice (stores/app.ts). A housekeeper must not lose her round to a
     * tab she never pressed.
     */
    const navigation = computed(() => [
        { name: 'today',       label: t('nav.today'),       icon: 'i-lucide-list-checks',    permission: Permission.HousekeepingRead },
        { name: 'sheet',       label: t('nav.sheet'),       icon: 'i-lucide-clipboard-list', permission: Permission.HousekeepingInspect },
        { name: 'teams',       label: t('nav.teams'),       icon: 'i-lucide-users',          permission: Permission.HousekeepingInspect },
        { name: 'board',       label: t('nav.board'),       icon: 'i-lucide-grid-3x3',       permission: Permission.HousekeepingInspect },
        { name: 'statistics',  label: t('nav.statistics'),  icon: 'i-lucide-chart-column',   permission: Permission.HousekeepingInspect },
        { name: 'stock',       label: t('nav.stock'),       icon: 'i-lucide-package',        permission: Permission.HousekeepingRead },
        { name: 'lost-items',  label: t('nav.lost_items'),  icon: 'i-lucide-package-search', permission: Permission.HousekeepingRead },
        { name: 'maintenance', label: t('nav.maintenance'), icon: 'i-lucide-wrench',         permission: Permission.MaintenanceRead },
        { name: 'my-rota',     label: t('nav.my_rota'),     icon: 'i-lucide-calendar-days',  module: StaffModule.Staff },
        { name: 'my-time',     label: t('nav.my_time'),     icon: 'i-lucide-clock',          module: StaffModule.Staff },
    ].filter(item => (item.permission ? appStore.can(item.permission) : true)
        && (item.module ? appStore.hasModule(item.module) : true)))

    const showNavigation = computed(() => navigation.value.length > 1)
</script>
